import type { ReactNode } from 'react';

/**
 * 统一加载/空/错误三态组件：与 .card/.btn/.badge 设计体系同一口径，
 * 替代各页面散落的纯文本「加载中… / 暂无…」提示。
 */

/** 表格骨架行（放入 <tbody>） */
export function SkeletonRows({ colSpan, rows = 5 }: { colSpan: number; rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="border-t border-slate-100">
          {Array.from({ length: colSpan }).map((__, j) => (
            <td key={j} className="px-4 py-4">
              <div
                className="h-3.5 rounded-full bg-slate-100 animate-pulse"
                style={{ width: `${52 + ((i * 11 + j * 17) % 42)}%` }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/** 块级骨架卡（卡片流加载态） */
export function SkeletonCards({ count = 4, className = '' }: { count?: number; className?: string }) {
  return (
    <div className={className}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="card card-pad animate-pulse flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-slate-100 shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-1/3 rounded-full bg-slate-100" />
            <div className="h-3 w-2/3 rounded-full bg-slate-100" />
          </div>
          <div className="h-4 w-16 rounded-full bg-slate-100" />
        </div>
      ))}
    </div>
  );
}

/** 行内旋转加载指示 */
export function Spinner({ label = '加载中…' }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm text-slate-400">
      <span className="inline-block w-4 h-4 rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
      {label}
    </span>
  );
}

/** 表格空态（放入 <tbody> 的一行） */
export function TableEmpty({
  colSpan, icon = '🗂️', title, hint, action,
}: { colSpan: number; icon?: string; title: string; hint?: string; action?: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-12">
        <div className="flex flex-col items-center justify-center text-center gap-1.5 animate-fade-in-up">
          <div className="w-12 h-12 rounded-2xl bg-brand-50 grid place-items-center text-2xl">{icon}</div>
          <p className="text-sm font-medium text-slate-600 mt-1">{title}</p>
          {hint && <p className="text-xs text-slate-400">{hint}</p>}
          {action && <div className="mt-2">{action}</div>}
        </div>
      </td>
    </tr>
  );
}

/** 块级空态卡 */
export function EmptyState({
  icon = '🗂️', title, hint, action,
}: { icon?: string; title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="card px-6 py-12 flex flex-col items-center justify-center text-center gap-1.5 animate-fade-in-up">
      <div className="w-14 h-14 rounded-2xl bg-brand-50 grid place-items-center text-3xl">{icon}</div>
      <p className="text-sm font-medium text-slate-600 mt-2">{title}</p>
      {hint && <p className="text-xs text-slate-400 max-w-sm">{hint}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

/** 错误横幅（可带重试） */
export function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="card p-4 border-red-200 bg-red-50/60 flex items-center justify-between gap-3 animate-fade-in-up">
      <p className="text-sm text-red-700">⚠ {message}</p>
      {onRetry && (
        <button onClick={onRetry} className="btn btn-ghost btn-sm shrink-0">重试</button>
      )}
    </div>
  );
}
